import { isNil, round, sum } from "lodash-es";
import { useEffect, useMemo } from "react";

import {
  calculateStudentOutcomeScore,
  filterAssessmentsForStudent,
  getAssessmentStatusLabel,
  getPageSpecificAssessmentData,
} from "./utils";

import { AssessmentStatus } from "api/api-data-types";
import { ASSESSMENT_VIEW_QUERY_PARAM } from "components/constants";
import { getPageType, getPageTypeShort, tableDateFormat } from "components/materials/page/helpers";
import { displayOutcomeName } from "components/outcomes/helpers";
import {
  ActivityType,
  CourseScoreDisplayFormat,
  PageGroupUserType,
  PageType,
  RubricType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  PageWithAssessmentsType,
  selectActivityMap,
  selectCurrentCourse,
  selectCurrentCourseId,
  selectCurrentOrg,
  selectFullCourseOutcomes,
  selectPageGroupUsers,
  selectPageOutcomes,
  selectPagesWithAssessments,
  selectSubmissions,
  selectUsersInCurrentCourse,
} from "store/selectors";
import { selectAllAssessmentInfo } from "store/selectors/computed";
import { NestedSectionOutcomeType, UserWithSectionUserType } from "store/selectors/types";
import { averageAndRound } from "utils/math";
import { useQueryParamsManager } from "utils/routing";
import { getUserFullName } from "utils/user-utils";

export const useIsAssessing = () => {
  const { getQueryParam } = useQueryParamsManager();
  const { canAuthorCourse } = useCapabilities();

  if (!canAuthorCourse) {
    return false;
  }
  // Instructors can assess, so check the query param
  // to see if they are currently assessing
  const isAssessing = getQueryParam(ASSESSMENT_VIEW_QUERY_PARAM);
  return !!isAssessing;
};

const filterPagesByType = (
  type: "assignment" | "prep_work" | "activities" | "other",
  publishedPagesWithAssessments: PageWithAssessmentsType[],
  activityMap: Record<string, ActivityType>,
) => publishedPagesWithAssessments.filter((page) => getPageTypeShort(page, activityMap) === type);

const getAssessmentPoints = (
  pageMap: { [id: string]: PageWithAssessmentsType },
  student: UserWithSectionUserType,
  pageGroupUsers: PageGroupUserType[],
): { assessedPoints: "-" | number; maxPoints: number } => {
  const studentPageAssessments = Object.values(pageMap).map((page) =>
    filterAssessmentsForStudent(page.assessments, student, pageGroupUsers),
  );

  if (studentPageAssessments.every((assessments) => assessments.length === 0))
    return { assessedPoints: "-", maxPoints: 0 };

  const filteredStudentPageAssessments = studentPageAssessments.filter(
    (assessments) => assessments.length > 0,
  );

  const assessedPoints = filteredStudentPageAssessments.reduce((totalPoints, assessments) => {
    const points = assessments.map((a) => a.points);
    return totalPoints + (points.length ? averageAndRound(points) : 0);
  }, 0);

  const maxPoints = filteredStudentPageAssessments.reduce((totalPoints, assessments) => {
    if (assessments.every((a) => isNil(a.points))) return totalPoints;
    return totalPoints + pageMap[assessments[0].submission.page_id].max_points;
  }, 0);

  return { assessedPoints, maxPoints };
};

const calculateWeightedPercent = (
  studentPoints: number | "-",
  totalPoints: number,
  maxValue: number,
  precision = 1,
) => {
  if (studentPoints === "-" || !maxValue || totalPoints === 0) return `-`;
  return round((studentPoints / totalPoints) * (maxValue / 100) * 100, precision);
};

export const calculateRubricScore = (orgRubric: RubricType, percent: number) => {
  if (!orgRubric) return "-";

  const baseLevel = orgRubric.findLast((level) => percent >= level.conversion_percent);
  const indexOfBaseLevel = orgRubric.findIndex(
    (level) =>
      level.score === baseLevel?.score &&
      level.conversion_percent === baseLevel?.conversion_percent,
  );

  if (!baseLevel || indexOfBaseLevel === -1) return "-";

  if (indexOfBaseLevel === orgRubric.length - 1) {
    return baseLevel.score;
  }

  const nextLevel = orgRubric[indexOfBaseLevel + 1];
  const percentDiff = nextLevel.conversion_percent - baseLevel.conversion_percent;
  const scoreDiff = nextLevel.score - baseLevel.score;
  const slope = scoreDiff / percentDiff;
  return round(baseLevel.score + slope * (percent - baseLevel.conversion_percent), 1);
};

const calculateTotalCourseGrade = (
  prepWorkCourseScore: number | "-",
  activitiesCourseScore: number | "-",
  assignmentsCourseScore: number | "-",
  prepWorkMaxScore: number,
  activitiesMaxScore: number,
  assignmentsMaxScore: number,
  courseScoreDisplayFormat: CourseScoreDisplayFormat,
  orgRubric: RubricType,
) => {
  if (
    (prepWorkCourseScore === "-" && !!prepWorkMaxScore) ||
    (activitiesCourseScore === "-" && !!activitiesMaxScore) ||
    (assignmentsCourseScore === "-" && !!assignmentsMaxScore)
  ) {
    return {
      totalCourseGradePercentDisplay: "-",
      totalCourseGradeForDisplay: "-",
      totalCourseGradeForDisplayIncludingMaxValue: "-",
    };
  }

  const courseScores = [
    prepWorkMaxScore ? prepWorkCourseScore : 0,
    activitiesMaxScore ? activitiesCourseScore : 0,
    assignmentsMaxScore ? assignmentsCourseScore : 0,
  ];

  const courseGrade = round(sum(courseScores.filter((score) => score !== "-")), 1);

  const courseGradeStr = `${courseGrade}%`;
  let totalCourseGradeForDisplay = courseGradeStr;
  let totalCourseGradeForDisplayIncludingMaxValue = courseGradeStr;

  if (
    courseScoreDisplayFormat === CourseScoreDisplayFormat.Scale_4 ||
    courseScoreDisplayFormat === CourseScoreDisplayFormat.Scale_5
  ) {
    totalCourseGradeForDisplay = `${calculateRubricScore(orgRubric, courseGrade)}`;
    totalCourseGradeForDisplayIncludingMaxValue = `${totalCourseGradeForDisplay} / ${courseScoreDisplayFormat === CourseScoreDisplayFormat.Scale_4 ? 4 : 5}`;
  }

  return {
    totalCourseGradePercentDisplay: courseGradeStr,
    totalCourseGradeForDisplay,
    totalCourseGradeForDisplayIncludingMaxValue,
  };
};

export type AssessmentDashboardDataType = {
  allAssessmentsCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  allAssessmentsTableRows: Array<{
    page: PageType;
    id: string;
    title: string;
    topicTitle: string | null;
    status: AssessmentStatus;
    unassessedLateCount: number;
    assessedSubmissionsCount: number;
    isAssessmentComplete: boolean;
    isMarkedComplete: boolean;
    publishing: string;
    assessedCount: number;
    pageType: string;
    avgPoints: string;
    outcomesCount: number;
    outcomesTooltip: string;
    lastSubmittedDate: string;
  }>;
  studentPointsPerPageCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  studentPointsPerPageTableRows: Array<{
    page: PageType;
    id: string;
    title: string;
    pageType: string;
    avgPoints: string;
    studentScores: (string | number)[];
  }>;
  studentScoresPerOutcomeCSVData: {
    headers: string[];
    rows: (string | number | null)[][];
  };
  studentScoresPerOutcomeTableRows: Array<{
    student: UserWithSectionUserType;
    studentName: string;
    outcomeScores: (number | null)[];
  }>;
  studentCourseGradesTableRows: Array<{
    studentName: string;
    id: string;
    activitiesMaxValue: number;
    activitiesPercentScore: string;
    assignmentsMaxValue: number;
    assignmentsPercentScore: string;
    prepWorkMaxValue: number;
    prepWorkPercentScore: string;
    /** The course grade in a percentage display should always be returned. Always included in reports. */
    totalCourseGradePercentDisplay: string;
    /**
     * The course grade in the configured display format (percentage or 0-X scale).
     * Included in reports if the org is configured to be displayed on the 0-X scale. */
    totalCourseGradeForDisplay: string;
    /**
     * The course grade in the configured display format including the max value
     * (e.g., X / 4 for 0-4 scale, X / 5 for 0-5 scale, X% for percent). This is
     * shown to the users on the student course grades table. */
    totalCourseGradeForDisplayIncludingMaxValue: string;
  }>;
  studentCourseGradesCSVData: {
    headers: string[];
    rows: string[][];
  };
  courseStudents: UserWithSectionUserType[];
  courseOutcomes: NestedSectionOutcomeType[];
  publishedPagesWithAssessments: PageType[];
};

export const useAssessmentDashboardData = () => {
  const course = useAppSelector(selectCurrentCourse);
  const courseId = useAppSelector(selectCurrentCourseId);
  const courseUsers = useAppSelector(selectUsersInCurrentCourse);
  const courseOutcomes = useAppSelector((s) => selectFullCourseOutcomes(s, courseId));
  const pageOutcomes = useAppSelector(selectPageOutcomes);
  const pageGroupUsers = useAppSelector(selectPageGroupUsers);
  const allSubmissions = useAppSelector(selectSubmissions);
  const pagesWithAssessments = useAppSelector(selectPagesWithAssessments);
  const allAssessmentInfo = useAppSelector(selectAllAssessmentInfo);
  const currentOrg = useAppSelector(selectCurrentOrg);
  const activityMap = useAppSelector(selectActivityMap);

  const courseScoreDisplayFormat =
    currentOrg?.course_score_display_format || CourseScoreDisplayFormat.Percent;

  const orgRubric = currentOrg?.rubric;

  useEffect(() => {
    if (courseId) {
      storeApi.course_users.list({ section_id: courseId });
      storeApi.course_outcomes.list({ section_id: courseId });
      storeApi.page_outcomes.list({ section_id: courseId });
      storeApi.page_groups.list({ section_id: courseId });
      storeApi.page_group_users.list({ section_id: courseId });
      storeApi.submissions.list({ section_id: courseId });
      storeApi.topics.list({ section_id: courseId });
      storeApi.activities.list({ section_id: courseId });
    }
  }, [courseId]);

  const courseStudents = courseUsers
    .filter((u) => u.role === "student")
    .sort((a, b) => getUserFullName(a).localeCompare(getUserFullName(b)));

  const pagesIdsWithSubmissions = useMemo(
    () => new Set(allSubmissions.map((submission) => submission.page_id)),
    [allSubmissions],
  );

  const allAssessments = pagesWithAssessments.map((p) => p.assessments).flat();
  // Here we specifically only want to show finalized assessments on pages with points
  const publishedPagesWithAssessments = pagesWithAssessments.filter(
    (page) => page.assessments_published_at && page.max_points,
  );
  const allScoredSubmissions = allAssessments.map((a) => a.submission);

  // Filter pages that have submissions and either max points or outcomes
  const filteredPagesWithAssessments = useMemo(
    () =>
      pagesWithAssessments.filter(
        (page) =>
          (page.max_points || pageOutcomes.some((po) => po.page_id === page.id)) &&
          pagesIdsWithSubmissions.has(page.id),
      ),
    [pagesWithAssessments, pageOutcomes, pagesIdsWithSubmissions],
  );

  const allAssessmentsCSVData = useMemo(() => {
    const headers = [
      t("assessment_dashboard.all.page_topic_header_title"),
      t("assessment_dashboard.all.page_header_title"),
      t("assessment_dashboard.all.status_header_title"),
      t("assessment_dashboard.all.publishing_header_title"),
      t("assessment_dashboard.all.assessed_header_title"),
      t("assessment_dashboard.all.page_type_header_title"),
      t("assessment_dashboard.all.avg_points_header_title"),
      t("assessment_dashboard.all.outcomes_header_title"),
      t("assessment_dashboard.all.last_submitted_header_title"),
    ];

    const rows = filteredPagesWithAssessments.map((page) => {
      const { pointList, latestSubmissionDate, pageSpecificOutcomes } =
        getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
      const assessmentStatus = allAssessmentInfo[page.id]?.status;
      const assessedSubmissionsCount = allAssessmentInfo[page.id]?.assessed_submissions_count || 0;
      const isMarkedComplete = Boolean(page.assessing_marked_complete_at);
      const isAssessmentComplete = assessmentStatus === AssessmentStatus.ASSESSED;

      return [
        page.topic?.title || "-",
        page.title,
        !assessmentStatus || assessmentStatus === AssessmentStatus.NOT_ASSESSABLE
          ? "-"
          : getAssessmentStatusLabel(
              isMarkedComplete,
              assessedSubmissionsCount,
              isAssessmentComplete,
            ),
        page.assessments_published_at ? "Published" : "Draft",
        page.assessments.length,
        getPageType(page, activityMap),
        pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
        pageSpecificOutcomes.length,
        latestSubmissionDate || "-",
      ];
    });

    return { headers, rows };
  }, [filteredPagesWithAssessments, pageOutcomes, activityMap, allAssessmentInfo]);

  const allAssessmentsTableRows = useMemo(
    () =>
      filteredPagesWithAssessments.map((page) => {
        const { pointList, latestSubmissionDate, pageSpecificOutcomes } =
          getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
        const pageSpecificOutcomeDisplayNames = pageSpecificOutcomes.flatMap((po) => {
          const courseOutcome = courseOutcomes.find((co) => co.id === po.section_outcome_id);
          return courseOutcome ? [displayOutcomeName(courseOutcome.outcome)] : [];
        });
        const outcomesTooltip = pageSpecificOutcomeDisplayNames.join(", ");

        return {
          page,
          id: page.id,
          title: page.title,
          topicTitle: page.topic?.title,
          status: allAssessmentInfo[page.id]?.status,
          unassessedLateCount: allAssessmentInfo[page.id]?.unassessed_late_count || 0,
          isMarkedComplete: Boolean(page.assessing_marked_complete_at),
          isAssessmentComplete: allAssessmentInfo[page.id]?.status === AssessmentStatus.ASSESSED,
          assessedSubmissionsCount: allAssessmentInfo[page.id]?.assessed_submissions_count || 0,
          publishing: page.assessments_published_at ? "Published" : "Draft",
          assessedCount: page.assessments.length,
          pageType: getPageType(page, activityMap),
          avgPoints: pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
          outcomesCount: pageSpecificOutcomes.length,
          outcomesTooltip,
          lastSubmittedDate: latestSubmissionDate || "-",
        };
      }),
    [filteredPagesWithAssessments, pageOutcomes, activityMap, courseOutcomes, allAssessmentInfo],
  );

  const studentPointsPerPageTableRows = useMemo(
    () =>
      publishedPagesWithAssessments.map((page) => {
        const { pointList } = getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
        const studentScores = courseStudents.map((student) => {
          const assessmentsForUser = filterAssessmentsForStudent(
            page.assessments,
            student,
            pageGroupUsers,
          );
          // Technically, this should always only be a single score,
          // but we average it (mean value) just in case
          const pointsForUser = assessmentsForUser.map((a) => a.points);
          return pointsForUser.length ? averageAndRound(pointsForUser) : "-";
        });
        return {
          page,
          id: page.id,
          title: page.title,
          pageType: getPageType(page, activityMap),
          avgPoints: pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
          studentScores,
        };
      }),
    [publishedPagesWithAssessments, pageOutcomes, activityMap, courseStudents, pageGroupUsers],
  );

  const studentPointsPerPageCSVData = useMemo(() => {
    const headers = [
      t("assessment_dashboard.all.page_header_title"),
      t("assessment_dashboard.all.page_type_header_title"),
      t("assessment_dashboard.all.avg_points_header_title"),
      ...courseStudents.map((student) => getUserFullName(student)),
    ];

    const rows = publishedPagesWithAssessments.map((page) => {
      const { pointList } = getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
      const studentScores = courseStudents.map((student) => {
        const assessmentsForUser = filterAssessmentsForStudent(
          page.assessments,
          student,
          pageGroupUsers,
        );
        const pointsForUser = assessmentsForUser.map((a) => a.points);
        return pointsForUser.length ? averageAndRound(pointsForUser) : "-";
      });

      return [
        page.title,
        getPageType(page, activityMap),
        pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
        ...studentScores,
      ];
    });

    return { headers, rows };
  }, [publishedPagesWithAssessments, courseStudents, pageGroupUsers, activityMap, pageOutcomes]);

  // TODO: Only show outcome scores for finalized assessments
  const studentScoresPerOutcomeTableRows = useMemo(
    () =>
      courseStudents.map((student) => {
        const userGroupIds = pageGroupUsers
          .filter((pgu) => pgu.section_user_id === student.section_user_id)
          .map((pgu) => pgu.page_group_id);

        const userSubmissions = allScoredSubmissions.filter(
          (submission) =>
            submission.section_user_id === student.section_user_id ||
            userGroupIds.includes(submission.page_group_id),
        );

        const userAssessments = allAssessments.filter((a) =>
          userSubmissions.find((submission) => submission.id === a.submission_id),
        );

        const outcomeScores = courseOutcomes.map((courseOutcome) => {
          return calculateStudentOutcomeScore(userAssessments, courseOutcome.id, pageOutcomes);
        });

        return {
          student,
          studentName: getUserFullName(student),
          outcomeScores,
        };
      }),
    [
      courseStudents,
      courseOutcomes,
      pageGroupUsers,
      allScoredSubmissions,
      allAssessments,
      pageOutcomes,
    ],
  );

  const studentScoresPerOutcomeCSVData = useMemo(() => {
    const headers = [
      t("assessment_dashboard.all.student_name_header_title"),
      ...courseOutcomes.map((courseOutcome) => displayOutcomeName(courseOutcome.outcome)),
    ];

    const rows = courseStudents.map((student) => {
      const userGroupIds = pageGroupUsers
        .filter((pgu) => pgu.section_user_id === student.section_user_id)
        .map((pgu) => pgu.page_group_id);

      const userSubmissions = allScoredSubmissions.filter(
        (submission) =>
          submission.section_user_id === student.section_user_id ||
          userGroupIds.includes(submission.page_group_id),
      );

      const userAssessments = allAssessments.filter((assessment) =>
        userSubmissions.find((submission) => submission.id === assessment.submission_id),
      );

      const outcomeScores = courseOutcomes.map((courseOutcome) => {
        return calculateStudentOutcomeScore(userAssessments, courseOutcome.id, pageOutcomes);
      });

      return [getUserFullName(student), ...outcomeScores];
    });

    return { headers, rows };
  }, [
    courseStudents,
    courseOutcomes,
    pageGroupUsers,
    allScoredSubmissions,
    allAssessments,
    pageOutcomes,
  ]);

  const studentCourseGradesTableRows = useMemo(() => {
    const courseGradeEnabled = course?.course_grade_enabled;
    if (!courseGradeEnabled) return [];

    const activitiesMaxValue = course?.course_grade_activities_percentage ?? 0;
    const prepWorkMaxValue = course?.course_grade_prepwork_percentage ?? 0;
    const assignmentsMaxValue = course?.course_grade_assignments_percentage ?? 0;

    const activityPageMap = filterPagesByType(
      "activities",
      publishedPagesWithAssessments,
      activityMap,
    ).reduce(
      (map, activityPage) => ({
        ...map,
        [activityPage.id]: activityPage,
      }),
      {},
    );

    const prepWorkPageMap = filterPagesByType(
      "prep_work",
      publishedPagesWithAssessments,
      activityMap,
    ).reduce(
      (map, activityPage) => ({
        ...map,
        [activityPage.id]: activityPage,
      }),
      {},
    );

    const assignmentPageMap = filterPagesByType(
      "assignment",
      publishedPagesWithAssessments,
      activityMap,
    ).reduce(
      (map, activityPage) => ({
        ...map,
        [activityPage.id]: activityPage,
      }),
      {},
    );

    return courseStudents.map((student) => {
      const {
        assessedPoints: studentPointsFromActivityPages,
        maxPoints: totalPointsFromActivityPages,
      } = getAssessmentPoints(activityPageMap, student, pageGroupUsers);

      const {
        assessedPoints: studentPointsFromPrepWorkPages,
        maxPoints: totalPointsFromPrepWorkPages,
      } = getAssessmentPoints(prepWorkPageMap, student, pageGroupUsers);

      const {
        assessedPoints: studentPointsFromAssignmentPages,
        maxPoints: totalPointsFromAssignmentPages,
      } = getAssessmentPoints(assignmentPageMap, student, pageGroupUsers);

      const activitiesPercentScore = calculateWeightedPercent(
        studentPointsFromActivityPages,
        totalPointsFromActivityPages,
        activitiesMaxValue,
      );
      const prepWorkPercentScore = calculateWeightedPercent(
        studentPointsFromPrepWorkPages,
        totalPointsFromPrepWorkPages,
        prepWorkMaxValue,
      );
      const assignmentsPercentScore = calculateWeightedPercent(
        studentPointsFromAssignmentPages,
        totalPointsFromAssignmentPages,
        assignmentsMaxValue,
      );

      const {
        totalCourseGradePercentDisplay,
        totalCourseGradeForDisplay,
        totalCourseGradeForDisplayIncludingMaxValue,
      } = calculateTotalCourseGrade(
        prepWorkPercentScore,
        activitiesPercentScore,
        assignmentsPercentScore,
        prepWorkMaxValue,
        activitiesMaxValue,
        assignmentsMaxValue,
        courseScoreDisplayFormat,
        orgRubric,
      );

      return {
        studentName: getUserFullName(student),
        id: student.id,
        activitiesMaxValue,
        activitiesPercentScore: `${activitiesPercentScore} / ${activitiesMaxValue}%`,
        assignmentsMaxValue,
        assignmentsPercentScore: `${assignmentsPercentScore} / ${assignmentsMaxValue}%`,
        prepWorkMaxValue,
        prepWorkPercentScore: `${prepWorkPercentScore} / ${prepWorkMaxValue}%`,
        totalCourseGradePercentDisplay,
        totalCourseGradeForDisplay,
        totalCourseGradeForDisplayIncludingMaxValue,
      };
    });
  }, [
    courseStudents,
    activityMap,
    course,
    pageGroupUsers,
    publishedPagesWithAssessments,
    courseScoreDisplayFormat,
    orgRubric,
  ]);

  const studentCourseGradesCSVData = useMemo(() => {
    if (studentCourseGradesTableRows.length === 0) {
      return { headers: [], rows: [] };
    }

    const maxPrepWorkPercent = studentCourseGradesTableRows[0].prepWorkMaxValue;
    const maxActivitiesPercent = studentCourseGradesTableRows[0].activitiesMaxValue;
    const maxAssignmentsPercent = studentCourseGradesTableRows[0].assignmentsMaxValue;

    // Need to escape because 18 / 20% gets reinterpreted as 1 once it's exported to csv.
    const escapeCsvValue = (value: string) => `="${value}"`;

    const headers = [
      t("assessment_dashboard.all.student_name_header_title"),
      maxPrepWorkPercent ? t("assessment_dashboard.grades.prepwork") : null,
      maxActivitiesPercent ? t("assessment_dashboard.grades.activities") : null,
      maxAssignmentsPercent ? t("assessment_dashboard.grades.assignments") : null,
      t("assessment_dashboard.student_course_grades.course_score_percent"),
      courseScoreDisplayFormat && courseScoreDisplayFormat !== CourseScoreDisplayFormat.Percent
        ? t(`assessment_dashboard.student_course_grades.course_score_${courseScoreDisplayFormat}`)
        : null,
    ].filter(Boolean);

    const rows = studentCourseGradesTableRows.map((row) =>
      [
        row.studentName,
        maxPrepWorkPercent ? escapeCsvValue(row.prepWorkPercentScore) : null,
        maxActivitiesPercent ? escapeCsvValue(row.activitiesPercentScore) : null,
        maxAssignmentsPercent ? escapeCsvValue(row.assignmentsPercentScore) : null,
        row.totalCourseGradePercentDisplay,
        courseScoreDisplayFormat && courseScoreDisplayFormat !== CourseScoreDisplayFormat.Percent
          ? row.totalCourseGradeForDisplay
          : null,
      ].filter(Boolean),
    );

    return { headers, rows };
  }, [studentCourseGradesTableRows, courseScoreDisplayFormat]);

  return {
    allAssessmentsCSVData,
    allAssessmentsTableRows,
    studentPointsPerPageCSVData,
    studentPointsPerPageTableRows,
    studentScoresPerOutcomeCSVData,
    studentScoresPerOutcomeTableRows,
    studentCourseGradesTableRows,
    studentCourseGradesCSVData,
    courseStudents,
    courseOutcomes,
    publishedPagesWithAssessments,
  };
};
